import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import NotFound from "./NotFound";
import RemovedPuzzle from "./RemovedPuzzle";
import { isRemovedPuzzleError, resolveLink } from "@/services/PropertyService";
import { useLiveVariant } from "@/context/LiveVariantContext";
import { logger } from "@/services/LoggerService";
import Loader from "@/components/common/loader/loader";
import { PUZZLE_OPEN_SCOREBOARD_KEY } from "@/constants/puzzle-storage.constants";
import { appendPuzzleViewToPath } from "@/helpers/puzzle-view-url";
import { withCurrentSearchParams } from "@/helpers/withCurrentSearchParams";

type ShortParams = {
    shortId?: string;
};

const ShortUrlRedirect: React.FC = () => {
    const { shortId } = useParams<ShortParams>();
    const { setLiveVariantState } = useLiveVariant();
    const navigate = useNavigate();
    const [isRedirecting, setIsRedirecting] = useState(true);
    const [showRemovedPuzzle, setShowRemovedPuzzle] = useState(false);

    useEffect(() => {
        const fetchLongLink = async () => {
            setShowRemovedPuzzle(false);

            try {
                const response = await resolveLink({ shortLink: shortId });
                const playableProperty = (response as any)?.data?.[0];
                const longLink = playableProperty?.longLink;
                const shortLink = playableProperty?.shortLink;

                if (shortLink) {
                    setLiveVariantState((prev) => ({
                        ...(prev as any),
                        shortLink,
                        playablePropertyId: playableProperty?.id,
                    }));
                }

                if (longLink) {
                    let targetPath = withCurrentSearchParams(`/puzzle${longLink}`);
                    try {
                        if (sessionStorage.getItem(PUZZLE_OPEN_SCOREBOARD_KEY) === "true") {
                            targetPath = appendPuzzleViewToPath(targetPath, "scoreboard");
                        }
                    } catch {
                        /* ignore */
                    }
                    navigate(targetPath, { replace: true });
                    return;
                }
            } catch (error) {
                if (isRemovedPuzzleError(error)) {
                    setShowRemovedPuzzle(true);
                } else {
                    setShowRemovedPuzzle(false);
                }
                console.error("Error resolving short link:", error);
                logger.error("Error resolving short link", error, {
                    shortId,
                    version: "puzzle",
                    context: "ShortUrlRedirect",
                });
            }
            setIsRedirecting(false);
        };

        fetchLongLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shortId]);

    if (isRedirecting) {
        return <Loader size={25} loading={true} text="Loading puzzle..." textSize="small" />;
    }

    if (showRemovedPuzzle) {
        return <RemovedPuzzle />;
    }

    return <NotFound />;
};

export default ShortUrlRedirect;
